import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import {
  commissionsApi,
  type ClaimTemplateDto,
  type UpsertClaimTemplateRequest,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { percent } from "@/components/commissions/formatters";

export function TemplatesPage() {
  const qc = useQueryClient();
  const templates = useQuery({
    queryKey: commissionKeys.templatesMine(),
    queryFn: () => commissionsApi.listTemplates(),
  });
  const [editing, setEditing] = useState<ClaimTemplateDto | null>(null);
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => commissionsApi.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionKeys.templates() }),
  });

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            My templates
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Personal templates for recurring commission types. Pre-fill pool, default description,
            and rate; the claim entry form picks them up automatically.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={14} strokeWidth={2} aria-hidden /> New template
        </Button>
      </div>

      {templates.isLoading ? (
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      ) : templates.data?.length === 0 ? (
        <Card>
          <div className="text-center py-space-4">
            <div className="font-display font-bold text-body text-onyx">No templates yet.</div>
            <div className="text-small text-gray-mid mt-space-1">
              Saved templates show up here and on the claim entry form.
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-space-3">
          {templates.data?.map((t) => (
            <Card key={t.id} className="flex flex-col gap-space-2">
              <div className="font-display font-bold text-body text-onyx">{t.name}</div>
              <div className="text-small text-gray-mid">
                <span className="font-display text-label font-semibold tracking-label">{t.poolCode}</span>{" "}
                {t.poolName}
              </div>
              {t.defaultCommissionPercent != null && (
                <div className="text-small text-gray-mid">
                  Default rate: <span className="font-mono">{percent(t.defaultCommissionPercent)}</span>
                </div>
              )}
              {t.defaultDescription && (
                <div className="text-small text-gray-mid border-t border-border-light pt-space-2">
                  {t.defaultDescription}
                </div>
              )}
              <div className="flex gap-space-2 pt-space-2 border-t border-border-light">
                <Button variant="secondary" size="sm" onClick={() => setEditing(t)}>
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  loading={remove.isPending}
                  onClick={() => remove.mutate(t.id)}
                >
                  <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && <TemplateEditor template={null} onClose={() => setCreating(false)} />}
      {editing && <TemplateEditor template={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

interface EditorProps {
  template: ClaimTemplateDto | null;
  onClose: () => void;
}

function TemplateEditor({ template, onClose }: EditorProps) {
  const qc = useQueryClient();
  const pools = useQuery({
    queryKey: commissionKeys.poolList("active"),
    queryFn: () => commissionsApi.listPools(false),
  });
  const [name, setName] = useState(template?.name ?? "");
  const [revenuePoolId, setRevenuePoolId] = useState(template?.revenuePoolId ?? "");
  const [defaultDescription, setDefaultDescription] = useState(template?.defaultDescription ?? "");
  const [defaultCommissionPercent, setDefaultCommissionPercent] = useState<string>(
    template?.defaultCommissionPercent != null ? String(template.defaultCommissionPercent) : "",
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload: UpsertClaimTemplateRequest = {
        name: name.trim(),
        revenuePoolId,
        defaultDescription: defaultDescription.trim() || null,
        defaultCommissionPercent:
          defaultCommissionPercent === "" ? null : Number(defaultCommissionPercent),
      };
      return template
        ? commissionsApi.updateTemplate(template.id, payload)
        : commissionsApi.createTemplate(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.templates() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const canSave = name.trim().length > 0 && revenuePoolId.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={template ? `Edit template "${template.name}"` : "New template"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}
      <div className="flex flex-col gap-space-3">
        <FormField label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="Pool" required>
          <select
            value={revenuePoolId}
            onChange={(e) => setRevenuePoolId(e.target.value)}
            className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
          >
            <option value="">Select a pool…</option>
            {pools.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Default description">
          <textarea
            value={defaultDescription}
            onChange={(e) => setDefaultDescription(e.target.value)}
            rows={3}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>
        <FormField label="Default commission %">
          <Input
            type="number"
            step="0.01"
            min={0}
            max={100}
            value={defaultCommissionPercent}
            onChange={(e) => setDefaultCommissionPercent(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  );
}
